import { useState, useCallback } from 'react';
import { receivedDemandsService } from '../../../services/receivedDemands';
import type { Demanda } from '../../../models';
import { toast } from '@/components/Toast/ToastProvider';

interface UseDemandSelectorProps {
  initialDemanda?: Demanda | null;
}

export const useDemandSelector = ({ initialDemanda }: UseDemandSelectorProps = {}) => {
  const [selectedDemanda, setSelectedDemanda] = useState<Demanda | null>(initialDemanda || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Função para buscar e selecionar uma demanda específica
  const selectDemandById = useCallback(async (demandaId: number) => {
    try {
      setLoading(true);
      setError(null);
      const demandaData = await receivedDemandsService.getDemanda(demandaId);
      
      if (!demandaData) {
        throw new Error('Demanda não encontrada');
      }
      
      setSelectedDemanda(demandaData);
      toast.success(`Demanda selecionada: ${demandaData.id}`);
      
      return demandaData;
    } catch (err) {
      console.error('Erro ao buscar demanda:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar demanda';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Função para selecionar uma demanda que já está carregada
  const selectDemand = useCallback((demanda: Demanda) => {
    setSelectedDemanda(demanda);
    setError(null);
  }, []);

  // Função para limpar a seleção
  const clearSelection = useCallback(() => {
    setSelectedDemanda(null);
    setError(null);
  }, []);

  // Função para atualizar a demanda atual
  const refreshSelectedDemand = useCallback(async () => {
    if (selectedDemanda?.id) {
      await selectDemandById(selectedDemanda.id);
    }
  }, [selectedDemanda?.id, selectDemandById]);

  return {
    selectedDemanda,
    loading,
    error,
    selectDemandById,
    selectDemand,
    clearSelection,
    refreshSelectedDemand,
  };
};